import {ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot} from "@angular/router";
import {catchError, EMPTY, forkJoin, Observable, of, switchMap} from "rxjs";
import {inject} from "@angular/core";
import {
  DEFAULT_PAGE_SIZE,
  League,
  LeagueFilter, Lookups, Organiser,
  PagedFilter,
  Pages, LeagueEvents
} from "@model";
import {AuthenticationService, CodebookService, LeagueEventsService, LeagueService} from "@services";
import {LANDING_ROUTE, ORGANISER_DASHBOARD, SERIES_ROUTE} from "../../app-routing.module";
import {parseIdListParam} from "@utility";
import {take} from "rxjs/operators";

export const LeaguesResolver: ResolveFn<Observable<League[]> |
    Promise<League[]> | League[]> | any = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    router: Router,
    leagueService: LeagueService = inject(LeagueService),
    codebookService: CodebookService = inject(CodebookService),
    authenticationService: AuthenticationService = inject(AuthenticationService)
): Observable<League[]> => {

  const organiserId = route.queryParams['organiser'] ? +route.queryParams['organiser'] : undefined;
  const leagueStatusId = route.queryParams['status'] ? +route.queryParams['status'] : undefined;
  const visibilityStatusId = route.queryParams['visibility'] ? +route.queryParams['visibility'] : undefined;
  const name = route.queryParams['name'] || undefined;

  const validStatuses$ = codebookService['leaguesStatuses'].value;
  const validOrganisers$ = codebookService['leagueOrganisers'].value;
  const validVisibilityStatuses$ = codebookService['visibilityStatuses'].value;

  return forkJoin([validStatuses$, validOrganisers$, validVisibilityStatuses$]).pipe(
      switchMap(([validStatuses, validOrganisers, validVisibilityStatuses]: [Lookups[], Organiser[], Lookups[]]) => {

        let isValid = true;

        if (organiserId !== undefined && !validOrganisers.some(o => o.id === organiserId)) {
          isValid = false;
        }
        if (leagueStatusId !== undefined && !validStatuses.some(s => s.id === leagueStatusId)) {
          isValid = false;
        }
        if (visibilityStatusId !== undefined && !validVisibilityStatuses.some(s => s.id === visibilityStatusId)) {
          isValid = false;
        }

        if (!isValid) {
          router.navigate([LANDING_ROUTE]);
          return EMPTY;
        }

        const filter = {
          name,
          leagueStatuses: leagueStatusId !== null && leagueStatusId !== undefined ? [leagueStatusId] : undefined,
          visibilityStatus: visibilityStatusId !== null && visibilityStatusId !== undefined ? [visibilityStatusId] : undefined,
          organiserId: authenticationService.isAdministrator() || authenticationService.isOrganisersAdministrator() ? organiserId : undefined
        };

        return leagueService.findLeagues(new PagedFilter(new Pages(DEFAULT_PAGE_SIZE, 1), filter));
      })
  );
};

export const LeagueResolver: ResolveFn<Observable<League | undefined> | League | undefined> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  leagueService: LeagueService = inject(LeagueService),
  router: Router = inject(Router)
): Observable<League | undefined> | League | undefined => {
  const leagueId = +route.queryParams['leagueId'];

  if (!leagueId) {
    router.navigate([ORGANISER_DASHBOARD]);
    return of(undefined);
  }

  return leagueService.findLeagueByIdProtected(leagueId, true).pipe(
      take(1),
      catchError(() => {
        router.navigate([ORGANISER_DASHBOARD]);
        return of(undefined);
      })
  );
};

export const LeagueEventsResolver: ResolveFn<Observable<LeagueEvents | undefined> | LeagueEvents | undefined> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  leagueEventsService: LeagueEventsService = inject(LeagueEventsService),
  router: Router = inject(Router)
): Observable<LeagueEvents | undefined> | LeagueEvents | undefined => {
  const leagueId = +route.queryParams['leagueId'];
  const isAdmin: boolean = route.data['isAdmin'] ?? false;
  const fallback = isAdmin ? ORGANISER_DASHBOARD : LANDING_ROUTE;

  if (!leagueId) {
    router.navigate([fallback]);
    return of(undefined);
  }

  const request$ = isAdmin
      ? leagueEventsService.getLeagueEventsProtected(leagueId)
      : leagueEventsService.getLeagueEvents(leagueId);

  return request$.pipe(
      take(1),
      catchError(() => {
        router.navigate([fallback]);
        return of(undefined);
      })
  );
};


export function createLeagueCalendarResolver(): ResolveFn<Observable<League[]> |
  Promise<League[]> | League[] | []> {
  return (route: ActivatedRouteSnapshot,
          state: RouterStateSnapshot,
          leagueService: LeagueService = inject(LeagueService),
          router: Router = inject(Router)):
    Observable<League[]> |
    Promise<League[]> | League[] | [] => {

    let [leagueFilter, paramsValid]: [LeagueFilter, boolean] = getParams(route);
    if (!paramsValid) {
      router.navigate([SERIES_ROUTE])
      return EMPTY;
    }

    return leagueService.findPublicLeagues(new PagedFilter(new Pages(), leagueFilter));
  };
}

function getParams(route: ActivatedRouteSnapshot): [LeagueFilter, boolean] {
  let valid: boolean = true;
  let leagueFilter: LeagueFilter = new LeagueFilter();

  let name = route.queryParams["name"] ? route.queryParams["name"] : "";
  let status = route.queryParams["status"];

  if (status) {
    leagueFilter.leagueStatuses = [+status];
  }

  if (name && name.trim().length > 0) {
    leagueFilter.name = name
  }

  type ArrayKeys = Extract<keyof LeagueFilter, 'sports' | 'countries'>;

  const keys: ArrayKeys[] = ['sports', 'countries'];

  keys.forEach((k) => {
    const { ids: paramIds, valid: paramValid } = parseIdListParam(route.queryParams, k);

    if (!paramValid) {
      valid = false;
    } else if (paramIds.length > 0) {
      leagueFilter[k] = paramIds;
    }
  });
  return [leagueFilter, valid];
}
